import React, { useState } from 'react';
import { uploadImageToCloudinary, CloudinaryUploadResult } from '../../../../app/helper/functions';

interface GalleryUploadFormProps {
    onSubmit: (imageUrls: string[]) => void;
}

const GalleryUploadForm: React.FC<GalleryUploadFormProps> = ({ onSubmit }) => {
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [previewUrls, setPreviewUrls] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({
        images: ''
    });

    // Handle multiple image selection
    const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const fileArray = Array.from(files);
        const validFiles: File[] = [];
        const newErrors: string[] = [];

        // First, validate all files
        fileArray.forEach((file, index) => {
            // Check if file is an image
            if (!file.type.match('image.*')) {
                newErrors.push(`File ${index + 1} is not an image`);
                return;
            }

            // Check file size (limit to 5MB)
            if (file.size > 5 * 1024 * 1024) {
                newErrors.push(`File ${index + 1} size should be less than 5MB`);
                return;
            }

            validFiles.push(file);
        });

        if (newErrors.length > 0) {
            setErrors({ images: newErrors.join(', ') });
            return;
        }

        setErrors({ images: '' });

        // Create previews for all valid files
        const previewPromises = validFiles.map((file) => {
            return new Promise<string>((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    resolve(reader.result as string);
                };
                reader.readAsDataURL(file);
            });
        });

        try {
            const newPreviews = await Promise.all(previewPromises);
            setSelectedFiles([...selectedFiles, ...validFiles]);
            setPreviewUrls([...previewUrls, ...newPreviews]);
        } catch (error) {
            console.error('Error creating previews:', error);
            setErrors({ images: 'Error processing images. Please try again.' });
        }
    };

    const removeImage = (index: number) => {
        const newFiles = selectedFiles.filter((_, i) => i !== index);
        const newPreviews = previewUrls.filter((_, i) => i !== index);
        setSelectedFiles(newFiles);
        setPreviewUrls(newPreviews);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (selectedFiles.length === 0) {
            setErrors({ images: 'Please select at least one image' });
            return;
        }

        setIsSubmitting(true);
        setErrors({ images: '' });

        try {
            // Upload all images to Cloudinary
            const uploadPromises = selectedFiles.map(file => uploadImageToCloudinary(file));
            const uploadResults = await Promise.all(uploadPromises);

            // Filter out any null values (failed uploads)
            const validUploads = uploadResults.filter(
                (result): result is string | CloudinaryUploadResult => result !== null
            );

            if (validUploads.length === 0) {
                setErrors({ images: 'Failed to upload images. Please try again.' });
                return;
            }

            // Normalize to plain URL strings
            const imageUrls = validUploads.map(result =>
                typeof result === 'string' ? result : result.url
            );

            if (imageUrls.length < uploadResults.length) {
                console.warn(`Some images failed to upload. ${imageUrls.length}/${uploadResults.length} uploaded successfully.`);
            }

            // Call the parent's onSubmit function with uploaded URLs
            onSubmit(imageUrls);

            // Reset form after successful submission
            setSelectedFiles([]);
            setPreviewUrls([]);
        } catch (error) {
            console.error('Error uploading images:', error);
            setErrors({ images: 'Failed to upload images. Please try again.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-blackDarkTheme rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4 text-white">
                Upload Gallery Images
            </h2>
            <form onSubmit={handleSubmit}>
                <div className="mb-4">
                    <label className="block text-gray-300 text-sm font-medium mb-2" htmlFor="gallery-images">
                        Select Images (Multiple)
                    </label>
                    <input
                        id="gallery-images"
                        type="file"
                        multiple
                        accept="image/*"
                        onChange={handleImageChange}
                        className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        disabled={isSubmitting}
                    />
                    {errors.images && <p className="mt-1 text-sm text-red-500">{errors.images}</p>}
                    <p className="mt-1 text-xs text-gray-400">
                        You can select multiple images at once. Maximum file size: 5MB per image.
                    </p>
                </div>

                {/* Preview Grid */}
                {previewUrls.length > 0 && (
                    <div className="mb-4">
                        <label className="block text-gray-300 text-sm font-medium mb-2">
                            Selected Images ({previewUrls.length})
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {previewUrls.map((preview, index) => (
                                <div key={index} className="relative group">
                                    <img
                                        src={preview}
                                        alt={`Preview ${index + 1}`}
                                        className="w-full h-32 object-cover rounded-md border border-gray-600"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeImage(index)}
                                        className="absolute top-1 right-1 bg-red-600 hover:bg-red-700 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                        disabled={isSubmitting}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="flex justify-end gap-4">
                    <button
                        type="submit"
                        disabled={isSubmitting || selectedFiles.length === 0}
                        className="bg-blue hover:bg-yellow text-white px-6 py-2 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? 'Uploading...' : `Upload ${selectedFiles.length} Image${selectedFiles.length !== 1 ? 's' : ''}`}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default GalleryUploadForm;

